import { useCallback } from 'react';
import immer from 'immer';
import { cloneDeep } from 'lodash';
import type {
  ApolloCache,
  Cache,
  DataProxy,
  DocumentNode,
  Reference,
  StoreObject,
  TypedDocumentNode,
  Unmasked,
} from '@apollo/client';
import { useApolloClient } from '@apollo/client';
import type { Modifier } from '@apollo/client/cache/core/types/common';
import { EMPTY_OBJECT } from '@frameio/utils/constants';
import type { ResolversParentTypes } from '../../generated';
/** update an Apollo query on the cache using `immer`
 * @param cache - the ApolloCache instance to read from
 * @param query - the query document
 * @param variables - the variables to pass to the query. pass {} for none.
 * @param update - an `immer` function which modifies the query response
 *
 * @deprecated use client.updateQuery with a TypedDocumentNode and immer separately instead
 */
export const updateQuery = <TQuery extends TypedDocumentNode<any, any>>(
  cache: ApolloCache<any>,
  query: TQuery,
  variables: DocVariables<TQuery>,
  update: (draft: DocResult<TQuery>) => DocResult<TQuery> | void,
  broadcast?: boolean,
  readOptions?: Omit<
    DataProxy.ReadQueryOptions<DocVariables<TQuery>, any>,
    'query' | 'id'
  >
) => {
  const existing = cache.readQuery({
    ...(readOptions || {}),
    query,
    variables,
  });

  const updated = immer(existing, update);

  return cache.writeQuery({
    query,
    data: updated,
    variables,
    broadcast,
  });
};

/**
 * typed wrapper for Apollo 3's cache.modify
 * pass in a type to the generic to get typing on the `fields`
 * @param cacheProxy - the ApolloCache instance to read from
 * @usage cacheModify<Comment>(cache, {fields:{...}})
 *
 */
export const cacheModify = <T>(
  cache: ApolloCache<any>,
  options: Omit<Cache.ModifyOptions, 'fields'> & {
    fields:
      | ModifierWithRefs<T>
      | {
          [K in keyof T]?: ModifierWithRefs<T[K]>;
        };
    identify?: Omit<Partial<StoreObject | Reference>, '__typename'> & {
      __typename?: keyof ResolversParentTypes;
      id?: string;
    };
  }
) => {
  let id = options?.id;
  if (options?.identify) {
    id = cache.identify(options.identify);
  }

  return cache.modify({ ...options, id } as Cache.ModifyOptions);
};

type CacheSnapshotOpts = {
  /** array of types to filter out of the snapshot  */
  typeFilter?: (keyof ResolversParentTypes)[];
};

/** returns a function that provides a snapshot of the current apollo cache */
export const useCacheSnapshot = (options?: CacheSnapshotOpts) => {
  const client = useApolloClient();

  return useCallback(() => {
    const { typeFilter } = options || {};
    let data: Record<
      string,
      {
        [K: string]: any;
        id?: string;
      }
    > = cloneDeep((client?.cache as any)?.data?.data);

    if (typeFilter) {
      const filtered: Record<any, any> = {};
      Object.entries(data).forEach((e: any) => {
        if (
          typeFilter.some(
            (filter) =>
              e[0].split(`:`)[0] === filter ||
              (filter === `Query` && e[0] === `ROOT_QUERY`) ||
              (filter === `Mutation` && e[0] === `ROOT_MUTATION`)
          )
        ) {
          filtered[e[0]] = e[1];
        }
      });
      data = filtered;
    }

    return data;
  }, [client?.cache, options]);
};

/** update an Apollo fragment on the cache using `immer`
 * @param cache - the ApolloCache instance to read/write
 * @param fragment - the fragment to update (import from `generated/ops`)
 * @param options - options for identifying and broadcasting the update
 *
 * @usage
 ```tsx
  import { updateFragment } from '@frameio/graphql-client';

  updateFragment<CompleteAccountUserFragment>({
    client.cache,
    CompleteAccountUser,
    {
      __typename: 'AccountUser',
      id: userId,
    },
    (draft) => {
      draft.name = 'lemmy';
    }
  });
 ```
 * @deprecated use client.updateFragment with a TypedDocumentNode and immer separately instead
 */
export const updateFragment = <T extends BaseFragment>(
  /** the apollo cache (you can get this from `client.cache` from the output of a query or mutation hook) */
  cache: ApolloCache<any>,
  /** the fragment doc (imported from /generated) */
  fragment: FragmentDoc,
  options: {
    /** the uuid of the fragment */
    id?: string;
    /** the fragment doc (imported from /generated) */
    __typename?: keyof ResolversParentTypes;
    /** __ref id of the fragment. alternate to providing id and __typename  */
    __ref?: string;
    /**
     * The name of the fragment defined in the fragment document to use in the call.
     * You don't need to provide this value if the fragment document includes only one fragment.
     */
    fragmentName?: string;
    /**
     * If true, reads & modifies the optimistically cached values for included
     * fields.
     * */
    optimistic?: boolean;
    /**
     * If true, automatically refresh all active queries that depend on at
     * least one field that's modified by this call.
     * */
    broadcast?: boolean;
    /** allow for reads of partial fragment data (true by default) */
    returnPartialData?: boolean;
  },
  update: (draft: T) => T | void
) => {
  const {
    __ref,
    __typename,
    id,
    fragmentName,
    optimistic = false,
    broadcast = true,
    returnPartialData = true,
  } = options;
  let cacheId: string | undefined;

  if (__ref) cacheId = cache.identify({ __ref });
  else {
    if (!id || !__typename)
      throw new Error(
        `must provide __ref or id and __typename to updateFragment`
      );
    cacheId = cache.identify({ id, __typename });
  }

  const cachedFragment = cache.readFragment<T>({
    fragment,
    fragmentName,
    id: cacheId,
    optimistic,
    returnPartialData,
  });

  const data = immer(cachedFragment, update);

  return cache.writeFragment<T>({
    data: data as Unmasked<T>,
    fragment,
    broadcast,
    id: cacheId,
    fragmentName,
  });
};

type BaseFragment = { __typename?: keyof ResolversParentTypes };
type FragmentDoc = DocumentNode | TypedDocumentNode<unknown, any>;
export type ModifierWithRefs<T> = (
  ...args: Parameters<Modifier<T>>
) => WithRefs<T>;

/** get the variable types from a typed document node */
export type DocVariables<T> = T extends TypedDocumentNode<unknown, infer V>
  ? V
  : never;

/** get the result types of a typed document node */
export type DocResult<T> = T extends TypedDocumentNode<infer R, unknown>
  ? R
  : never;

/**
 * widen T recursively such that all objects and sub-objects allow an Apollo
 * `Reference` shape in addition to their own shape
 *
 * you can optionally pass `false` to `TIncludeRoot` to exclude the root type from being a Reference
 * */
export type WithRefs<T, TIncludeRoot = true, TIsRoot = true> = T extends object
  ?
      | {
          [K in keyof T]: T[K] extends object
            ? WithRefs<T[K], TIncludeRoot, false>
            : T[K];
        }
      | (TIsRoot extends false
          ? Reference
          : TIncludeRoot extends true
          ? Reference
          : never)
  : T;

/**
 * Returns the query arguments for a given field from an Apollo cache entry.
 * @param storeFieldName The `storeFieldName` for this cached entry (includes query arguments)
 * @param fieldName The generic field name
 * @returns Query arguments associated for that Apollo cache key
 */
export function parseFieldArguments<V extends Record<string, any>>(
  storeFieldName: string,
  fieldName: string
): V | Record<string, never> {
  const cacheKey = `${fieldName}:`;

  if (!storeFieldName.includes(cacheKey)) {
    return EMPTY_OBJECT as Record<string, never>;
  }

  // `indexOf` here is mostly superfluous, since it _should_ always return `0`
  const cacheKeyIndex = storeFieldName.indexOf(cacheKey) + cacheKey.length;
  const args = storeFieldName.slice(cacheKeyIndex);

  /**
   * `args` should alwyas exist and should be valid JSON here.
   * `parseFieldArguments` is probably being called on incorrect inputs
   * if `JSON.parse` throws here.
   */
  return args ? JSON.parse(args) : (EMPTY_OBJECT as Record<string, never>);
}
